/**
 * Hook for Blackjack Multiplayer game state
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  TableData,
  TablePlayer,
  subscribeToTable,
  startRound,
  placeBet,
  dealCards,
  hit as hitAction,
  stand as standAction,
  double as doubleAction,
  playDealerTurn,
  leaveTable,
  sendChatMessage,
  calculateHandValue,
} from '../services/multiplayerService'

interface UseMultiplayerGameReturn {
  // Table state
  tableData: TableData | null
  isLoading: boolean
  error: string | null

  // My state
  myPlayer: TablePlayer | null
  isCapo: boolean
  isMyTurn: boolean
  isSpectator: boolean

  // Timer
  timeLeft: number

  // Computed
  dealerValue: number
  showDealerCards: boolean
  canBet: boolean
  canPlay: boolean
  canDouble: boolean

  // Actions
  handleStartRound: () => Promise<void>
  handlePlaceBet: (amount: number) => Promise<{ success: boolean; error?: string }>
  handleHit: () => Promise<void>
  handleStand: () => Promise<void>
  handleDouble: () => Promise<void>
  handleLeave: () => Promise<void>
  handleSendChat: (message: string) => Promise<void>
}

export function useMultiplayerGame(tableId: string): UseMultiplayerGameReturn {
  const { user, updateBalance } = useAuth()
  const [tableData, setTableData] = useState<TableData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const prevTurnSeatRef = useRef<number | null>(null)

  // Subscribe to table updates
  useEffect(() => {
    if (!tableId) return

    const unsubscribe = subscribeToTable(tableId, (data) => {
      if (data) {
        setTableData(data)
        setIsLoading(false)
      } else {
        setError('Mesa no encontrada o cerrada')
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [tableId])

  // My player info
  const myPlayer = tableData?.players.find((p) => p.odIdBrief === user?.uid) || null
  const isCapo = tableData?.capoId === user?.uid
  const isSpectator = !myPlayer && tableData?.spectators?.includes(user?.uid || '') || false
  const isMyTurn = tableData?.currentTurnSeat === myPlayer?.seat && tableData?.gameState === 'playing'

  // Handle turn timer
  useEffect(() => {
    if (isMyTurn && prevTurnSeatRef.current !== tableData?.currentTurnSeat) {
      // New turn - start timer
      setTimeLeft(30)
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            // Auto-stand when time runs out
            handleStand()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else if (!isMyTurn && timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
      setTimeLeft(0)
    }

    prevTurnSeatRef.current = tableData?.currentTurnSeat || null

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isMyTurn, tableData?.currentTurnSeat])

  // Computed values
  const showDealerCards =
    tableData?.gameState === 'dealerTurn' || tableData?.gameState === 'ended'
  const dealerValue = showDealerCards
    ? calculateHandValue(tableData?.dealerHand || [])
    : tableData?.dealerHand?.[0]
      ? calculateHandValue([tableData.dealerHand[0]])
      : 0

  const canBet = tableData?.gameState === 'betting' && myPlayer?.status !== 'ready'
  const canPlay = isMyTurn && tableData?.gameState === 'playing'
  const canDouble = canPlay && myPlayer?.hand?.length === 2 && (user?.balance || 0) >= (myPlayer?.currentBet || 0)

  // Check if all players have bet (for Capo to deal)
  useEffect(() => {
    if (!isCapo || tableData?.gameState !== 'betting') return

    const allReady = tableData?.players.every((p) => p.status === 'ready' && p.currentBet > 0)
    if (allReady) {
      dealCards(tableId)
    }
  }, [isCapo, tableId, tableData?.gameState, tableData?.players])

  // Trigger dealer turn when needed (Capo only)
  useEffect(() => {
    if (!isCapo || tableData?.gameState !== 'dealerTurn') return

    const timer = setTimeout(() => {
      playDealerTurn(tableId)
    }, 1000)

    return () => clearTimeout(timer)
  }, [isCapo, tableId, tableData?.gameState])

  // Actions
  const handleStartRound = useCallback(async () => {
    if (!isCapo) return
    await startRound(tableId)
  }, [isCapo, tableId])

  const handlePlaceBet = useCallback(
    async (amount: number): Promise<{ success: boolean; error?: string }> => {
      if (!user || !myPlayer) return { success: false, error: 'No autorizado' }

      if (amount < (tableData?.minBet || 100)) {
        return { success: false, error: `Apuesta mínima: $${tableData?.minBet || 100}` }
      }

      if (amount > user.balance) {
        return { success: false, error: 'Saldo insuficiente' }
      }

      // Deduct from balance
      await updateBalance(-amount)

      const result = await placeBet(tableId, user.uid, amount)
      if (!result.success) {
        // Refund if failed
        await updateBalance(amount)
      }

      return result
    },
    [user, myPlayer, tableId, tableData?.minBet, updateBalance]
  )

  const handleHit = useCallback(async () => {
    if (!canPlay || !user) return
    await hitAction(tableId, user.uid)
  }, [canPlay, tableId, user])

  const handleStand = useCallback(async () => {
    if (!canPlay || !user) return
    await standAction(tableId, user.uid)
  }, [canPlay, tableId, user])

  const handleDouble = useCallback(async () => {
    if (!canDouble || !user || !myPlayer) return

    // Deduct additional bet
    await updateBalance(-myPlayer.currentBet)
    await doubleAction(tableId, user.uid)
  }, [canDouble, tableId, user, myPlayer, updateBalance])

  const handleLeave = useCallback(async () => {
    if (!user) return
    await leaveTable(tableId, user.uid)
  }, [tableId, user])

  const handleSendChat = useCallback(
    async (message: string) => {
      if (!user) return
      await sendChatMessage(tableId, user.uid, user.displayName || 'Jugador', message)
    },
    [tableId, user]
  )

  return {
    tableData,
    isLoading,
    error,
    myPlayer,
    isCapo,
    isMyTurn,
    isSpectator,
    timeLeft,
    dealerValue,
    showDealerCards,
    canBet,
    canPlay,
    canDouble,
    handleStartRound,
    handlePlaceBet,
    handleHit,
    handleStand,
    handleDouble,
    handleLeave,
    handleSendChat,
  }
}
